import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType, Scope } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type ProposalsAccessJSON = z.infer<typeof ProposalsAccessSchema.ProposalsAccessJSONZ>;

export type ProposalsAccessParams = z.infer<typeof ProposalsAccessSchema.ProposalsAccessZ>;

export default class ProposalsAccessSchema {
  public static readonly ProposalsAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.ProposalsAccess),
    accessLevel: z.enum(AccessLevel).nullish(),
    scope: z.enum(Scope).nullish(),
  });

  public static readonly ProposalsAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.ProposalsAccess),
    accessLevel: z.enum(AccessLevel).optional(),
    scope: z.enum(Scope).optional(),
  });

  public static validate(values: unknown): ProposalsAccessParams {
    return parseOrThrow(ProposalsAccessSchema.ProposalsAccessZ, values, {
      logError: true,
      schemaName: "ProposalsAccessSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): ProposalsAccessJSON {
    return parseOrThrow(ProposalsAccessSchema.ProposalsAccessJSONZ, values, {
      logError: true,
      schemaName: "ProposalsAccessSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is ProposalsAccessParams {
    try {
      ProposalsAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is ProposalsAccessJSON {
    try {
      ProposalsAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: ProposalsAccessJSON): ProposalsAccessParams {
    const parsed = parseOrThrow(ProposalsAccessSchema.ProposalsAccessJSONZ, data, {
      logError: true,
      schemaName: "ProposalsAccessSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return ProposalsAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: ProposalsAccessParams): ProposalsAccessJSON {
    const parsed = ProposalsAccessSchema.validate(notification);
    return ProposalsAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    ProposalsAccessSchema.ProposalsAccessJSONZ,
    ProposalsAccessSchema.ProposalsAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.ProposalsAccess as const,
        accessLevel: data.accessLevel ?? undefined,
        scope: data.scope ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.ProposalsAccess as const,
        accessLevel: resource.accessLevel,
        scope: resource.scope,
      }),
    }
  );
}
